import { ContentType } from '@amzn/awscat-pattern-library-service-model';
import React from 'react';
import constants from '../../../common/constants';
import ModalDialogButtonV3 from '../../common/ModalDialogButtonV3';
import WorkingCopyStatus, { WorkingCopyStatusType } from '../../common/WorkingCopyStatus';
import { Container, SpaceBetween } from '@amzn/awsui-components-core';

interface ContentDataControlsProps {
  isEditingEnabled: boolean;
  isSaveDisabled: boolean;
  onEnableEditing?: () => void;
  onDiscardChanges?: () => void;
  onSaveChanges?: (unlock: boolean) => void;
  onBeforeSaving?: () => Promise<boolean>;
  contentType?: ContentType;
  onlyActionButtons?: boolean;
  disableButtons?: boolean;
  workingCopyStatus: WorkingCopyStatusType;
  isInPeerReview: boolean;
}

class ContentDataControls extends React.Component<ContentDataControlsProps, { isSaving: boolean }> {
  state = { isSaving: false };

  public render() {
    const { onlyActionButtons } = this.props;
    const controls = !onlyActionButtons ? (
      <Container>{this.renderButtons()}</Container>
    ) : (
      <div>{this.renderButtons()}</div>
    );
    return controls;
  }

  private renderButtons = () => {
    const { isEditingEnabled, isSaveDisabled, contentType, disableButtons, isInPeerReview } =
      this.props;
    const { isSaving } = this.state;

    if (isEditingEnabled) {
      const modalText = isSaveDisabled
        ? constants.WORKFLOW_EDIT_IN_REVIEW_EXIT_MODAL_TEXT
        : constants.WORKFLOW_EDIT_IN_REVIEW_DISCARD_MODAL_TEXT;
      const buttonText = isSaveDisabled
        ? constants.WORKFLOW_EDIT_IN_REVIEW_EXIT_BUTTON_TEXT
        : constants.WORKFLOW_EDIT_IN_REVIEW_DISCARD_BUTTON_TEXT;
      const saveModalText = isInPeerReview
        ? constants.WORKFLOW_SAVE_PEER_REVIEW_TEXT
        : constants.WORKFLOW_EDIT_SAVE_MODAL_TEXT;
      const saveButtonText = isInPeerReview
        ? constants.WORKFLOW_EDIT_PEER_REVIEW_EXIT_BUTTON_TEXT
        : constants.WORKFLOW_EDIT_SAVE_AND_CLOSE_BUTTON_TEXT;
      return (
        <SpaceBetween size="xs">
          <WorkingCopyStatus workingCopyStatus={this.props.workingCopyStatus} />
          <ModalDialogButtonV3
            variant="primary"
            header={constants.WORKFLOW_EDIT_SAVE_MODAL_HEADER}
            text={saveModalText}
            submitButtonText={saveButtonText}
            onBeforeOpenModal={this.validateBeforeSave}
            onSubmit={this.saveChanges}
            disabled={isSaving}
            fullwidth
            dataTestId="right-column-edit-action-save-and-close-modal"
          />
          <ModalDialogButtonV3
            variant="normal"
            header={constants.WORKFLOW_EDIT_HEADER}
            text={modalText}
            submitButtonText={buttonText}
            onSubmit={this.discardChanges}
            disabled={isSaving}
            fullwidth
            dataTestId="right-column-edit-action-discard-changes-modal"
          />
        </SpaceBetween>
      );
    } else {
      const modalText = isInPeerReview
        ? constants.WORKFLOW_EDIT_PEER_REVIEW_TEXT
        : constants.WORKFLOW_EDIT_MODAL_TEXT;
      return (
        <ModalDialogButtonV3
          variant="normal"
          header={constants.WORKFLOW_EDIT_HEADER}
          text={modalText}
          submitButtonText={
            contentType ? `Edit ${contentType}` : constants.WORKFLOW_EDIT_MODAL_SUBMIT_BUTTON_TEXT
          }
          onSubmit={this.enableEditing}
          disabled={disableButtons}
          fullwidth
          iconName={'edit'}
          iconAlign={'left'}
          dataTestId={`right-column-readonly-action-edit-${contentType?.toLowerCase()}`}
        />
      );
    }
  };

  private enableEditing = async () => {
    const { onEnableEditing } = this.props;
    if (onEnableEditing) {
      await onEnableEditing();
    }
  };

  private discardChanges = async () => {
    const { onDiscardChanges } = this.props;
    if (onDiscardChanges) {
      await onDiscardChanges();
    }
  };

  private saveChanges = async () => {
    const { onSaveChanges } = this.props;
    if (onSaveChanges) {
      await onSaveChanges(true);
    }
  };

  private validateBeforeSave = async (): Promise<boolean> => {
    const { onBeforeSaving } = this.props;
    if (onBeforeSaving) {
      return await onBeforeSaving();
    }
    return true;
  };
}

export default ContentDataControls;
